import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import useTransactions from '../../hooks/useTransactions';
import DashboardSummary from '../SummaryCard';
import TransactionTable from '../TransactionTable';
import Modal from '../Modal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const UpdateStatusPage: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusPaid, setStatusPaid] = useState('');
  const [status, setStatus] = useState('');
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const { data, loading, error, domains, refetch } = useTransactions(domain, startDate, endDate, statusPaid, status);

  const { totalNetAmount, totalUnpaidAmount, totalPaidAmount, selectedTotalAmount, selectedDomains } = useMemo(() => {
    if (!data) return { totalNetAmount: 0, totalUnpaidAmount: 0, totalPaidAmount: 0, selectedTotalAmount: 0, selectedDomains: [] };

    const totalNetAmount = data.data.summary.total_amount;
    const totalUnpaidAmount = data.data.transactions
      .filter(t => !t.status_paid)
      .reduce((sum, t) => sum + t.amount, 0);
    const totalPaidAmount = data.data.transactions
      .filter(t => t.status_paid)
      .reduce((sum, t) => sum + t.amount, 0);
    const selectedTransactionsData = data.data.transactions.filter(t => selectedTransactions.includes(t.id));
    const selectedTotalAmount = selectedTransactionsData.reduce((sum, t) => sum + t.amount, 0);
    const selectedDomains = Array.from(new Set(selectedTransactionsData.map(t => t.domain)));

    return { totalNetAmount, totalUnpaidAmount, totalPaidAmount, selectedTotalAmount, selectedDomains };
  }, [data, selectedTransactions]);

  const handleDomainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setDomain(event.target.value);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleStatusPaidChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusPaid(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value);
  };

  const handleTransactionSelect = (id: number) => {
    setSelectedTransactions(prev => 
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (!data) return; // เพิ่มการตรวจสอบนี้
    if (selectedTransactions.length === data.data.transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(data.data.transactions.map(t => t.id));
    }
  };

  const generatePDF = useCallback(() => {
    console.log('เริ่มฟังก์ชัน generatePDF');
    if (!data || selectedTransactions.length === 0) {
      console.log('ไม่มีข้อมูลหรือไม่มีรายการที่เลือกสำหรับสร้าง PDF');
      return;
    }

    console.log('กำลังสร้าง PDF...');

    try {
      const doc = new jsPDF();
      doc.setFont('THSarabunNew', 'normal');
      doc.setFontSize(20);

      // หัวข้อ
      doc.text('ใบเรียกเก็บเงิน', 105, 15, { align: 'center' });

      // ข้อมูลบริษัท
      doc.setFontSize(12);
      doc.text('บริษัท ของคุณ จำกัด', 15, 30);
      doc.text('123 ถนนสุขุมวิท แขวงคลองเตย', 15, 35);
      doc.text('เขตคลองเตย กรุงเทพฯ 10110', 15, 40);
      doc.text('โทร: 02-123-4567', 15, 45);

      // ข้อมูลลูกค้า
      doc.text('ลูกค้า:', 15, 60);
      doc.text(`โดเมน: ${selectedDomains.join(', ')}`, 15, 65);

      // สรุปข้อมูล
      doc.text(`จำนวนรายการ: ${selectedTransactions.length}`, 15, 80);
      doc.text(`ยอดรวมทั้งสิ้น: ${selectedTotalAmount.toLocaleString()} บาท`, 15, 85);

      // สร้างตาราง
      const tableColumn = ["ลำดับ", "รายการ", "จำนวนเงิน"];
      const tableRows = data.data.transactions
        .filter(t => selectedTransactions.includes(t.id))
        .map((t, index) => [
          index + 1,
          `รายการ #${t.id}`,
          `${t.amount.toLocaleString()} บาท`
        ]);

      (doc as any).autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 95,
        theme: 'grid',
        styles: { font: 'THSarabunNew', fontSize: 12 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      });

      console.log('PDF ถูกสร้างเสร็จแล้ว กำลังดาวน์โหลด...');

      doc.save('ใบเรียกเก็บเงิน.pdf');

      console.log('การดาวน์โหลด PDF เสร็จสิ้น');
    } catch (error) {
      console.error('เกิดข้อผิดพลาดในการสร้าง PDF:', error);
    }
  }, [data, selectedTransactions, selectedTotalAmount, selectedDomains]);

  const handleUpdateStatusPaid = async () => {
    if (!data) return;
    if (selectedTransactions.length === 0) {
      alert('กรุณาเลือกรายการที่ต้องการอัพเดท');
      return;
    }
    setIsModalOpen(true);
    // if (selectedTransactions.length === data.data.transactions.length) {
    //   generatePDF();
    // }
  };

  const confirmUpdate = async () => {
    setIsUpdating(true);
    try {
      const response = await axios.post('https://azpay.devng.pro/api/transactions/update-status-paid', {
        id: selectedTransactions
      });
      console.log('ผลลัพธ์การอัพเดท:', response.data);
      setUpdateSuccess(true);
      
      // รีเฟรชข้อมูลทันที
      await refetch();

      // // เพิ่ม log และเงื่อนไขการตรวจสอบ
      // console.log('จำนวนรายการที่เลือก:', selectedTransactions.length);
      // console.log('จำนวนรายการทั้งหมด:', data?.data.transactions.length);

      // // เรียกใช้ generatePDF() ทันทีหลังจากอัพเดทสำเร็จและรีเฟรชข้อมูล
      // if (selectedTransactions.length === data?.data.transactions.length) {
      //   console.log('เงื่อนไขสำหรับการสร้าง PDF เป็นจริง, กำลังเรียก generatePDF()');
      //   setTimeout(() => {
      //     generatePDF();
      //   }, 2000); // เพิ่มเวลารอเป็น 2 วินาที
      // } else {
      //   console.log('ไม่ได้เลือกรายการทั้งหมด, ไม่สร้าง PDF');
      // }
    } catch (error) {
      console.error('เกิดข้อผิดพลาดในการอัพเดทสถานะ:', error);
      setUpdateSuccess(false);
    } finally {
      setIsUpdating(false);
      setIsModalOpen(false);
      setSelectedTransactions([]); // รีเซ็ตรายการที่เลือก
    }
  };

  const summaryData = useMemo(() => {
    if (!data) return {
      totalPayAmount: 0,
      successfulTotalPayAmount: 0,
      unsuccessfulTotalPayAmount: 0,
      totalNetAmount: 0,
      successfulTotalNetAmount: 0,
      unsuccessfulTotalNetAmount: 0,
      totalFee: 0,
      successfulTotalFee: 0,
      unsuccessfulTotalFee: 0,
      totalUnpaidAmount: 0,
      totalPaidAmount: 0,
      transactionCount: 0
    };

    const successfulTransactions = data.data.transactions.filter(t => t.status === 1);
    const unsuccessfulTransactions = data.data.transactions.filter(t => t.status !== 1);

    return {
      totalPayAmount: data.data.summary.total_payAmount,
      successfulTotalPayAmount: successfulTransactions.reduce((sum, t) => sum + t.payAmount, 0),
      unsuccessfulTotalPayAmount: unsuccessfulTransactions.reduce((sum, t) => sum + t.payAmount, 0),
      totalNetAmount: data.data.summary.total_amount,
      successfulTotalNetAmount: successfulTransactions.reduce((sum, t) => sum + t.amount, 0),
      unsuccessfulTotalNetAmount: unsuccessfulTransactions.reduce((sum, t) => sum + t.amount, 0),
      totalFee: data.data.summary.total_fee,
      successfulTotalFee: successfulTransactions.reduce((sum, t) => sum + t.fee, 0),
      unsuccessfulTotalFee: unsuccessfulTransactions.reduce((sum, t) => sum + t.fee, 0),
      totalUnpaidAmount: successfulTransactions.filter(t => !t.status_paid).reduce((sum, t) => sum + t.amount, 0),
      totalPaidAmount: successfulTransactions.filter(t => t.status_paid).reduce((sum, t) => sum + t.amount, 0),
      transactionCount: data.data.count
    };
  }, [data]);

  if (loading) return <div>กำลังโหลด...</div>;
  if (error) return <div>เกิดข้อผิดพลาด: {error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">อัพเดทสถานะการเบิกเงิน</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">โดเมน</label>
          <select
            value={domain}
            onChange={handleDomainChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทุก Domain</option>
            {Array.isArray(domains) && domains.length > 0 ? (
              domains.map((d: string) => (
                <option key={d} value={d}>{d}</option>
              ))
            ) : (
              <option disabled>ไม่พบโดเมน</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่เริ่มต้น</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่สิ้นสุด</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการเบิก</label>
          <select
            value={statusPaid}
            onChange={handleStatusPaidChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">ยังไม่เบิก</option>
            <option value="1">เบิกแล้ว</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการทำรายการ</label>
          <select
            value={status}
            onChange={handleStatusChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">รอดำเนินการ</option>
            <option value="1">สำเร็จ</option>
          </select>
        </div>
      </div>
      <DashboardSummary data={summaryData} />
      <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">ข้อมูลที่เลือก</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">จำนวนรายการที่เลือก</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{selectedTransactions.length} รายการ</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">ยอดรวมที่เลือก</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{selectedTotalAmount.toLocaleString()} บาท</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">เว็บที่เลือก</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {selectedDomains.length > 0 ? (
                  <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                    {selectedDomains.map((domain, index) => (
                      <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                        <div className="w-0 flex-1 flex items-center">
                          <span className="ml-2 flex-1 w-0 truncate">{domain}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-gray-500">ไม่มีเว็บที่เลือก</span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <div className="mt-8 flex justify-between items-center">
        <button
          onClick={handleSelectAll}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          {selectedTransactions.length === data.data.transactions.length ? 'ยกเลิกเลือกทั้งหมด' : 'เลือกทั้งหมด'}
        </button>
        <button
          onClick={() => {
            console.log('ปุ่มอัพเดทสถานะถูกคลิก'); // เพิ่ม log
            handleUpdateStatusPaid();
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          อัพเดทสถานะการเบิกเงิน
        </button>
      </div>
      <TransactionTable 
        transactions={data.data.transactions} 
        onTransactionSelect={handleTransactionSelect}
        selectedTransactions={selectedTransactions}
      />
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2 className="text-xl font-bold mb-4">ยืนยันการอัพเดทสถานะ</h2>
        <p>คุณต้องการอัพเดทสถานะการเบิกเงินสำหรับ {selectedTransactions.length} รายการ</p>
        <p>ยอดรวมที่เลือก: {selectedTotalAmount.toLocaleString()} บาท</p>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={() => setIsModalOpen(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            ยกเลิก
          </button>
          <button
            onClick={confirmUpdate}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isUpdating}
          >
            {isUpdating ? 'กำลังอัพเดท...' : 'ยืนยัน'}
          </button>
        </div>
        {updateSuccess !== null && (
          <div className={`mt-4 p-2 rounded ${updateSuccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {updateSuccess ? 'อัพเดทสถานะสำเร็จ' : 'เกิดข้อผิดพลาดในการอัพเดทสถานะ'}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UpdateStatusPage;